import React, { useState, useEffect, useRef } from "react";
import { estadoFormacionLabel } from "../constants/estadosFormacion";
import { nombreCompleto } from "../adapters/formacionesAdapter";

const formatDate = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    const datePart = value.split("T")[0].split(" ")[0];
    const matchIso = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matchIso) {
      const [, year, month, day] = matchIso;
      return `${day}/${month}/${year}`;
    }
    const matchSlash = datePart.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (matchSlash) return datePart;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

export default function FormacionesTable({
  formaciones,
  loading,
  reload,
  error,
  seleccionadoId,
  onSelect,
  onEdit,
  onDelete,
  onViewHistorial,
  maxHeight = "60vh",
  canEdit = true,
  canDelete = true,
}) {
  const minRows = 10;
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <span className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error) {
    return <div className="management-card management-alert">{error}</div>;
  }

  if (!formaciones.length) {
    return (
      <div className="management-card dashboard-empty text-center py-5">
        <i className="bi bi-journal-text dashboard-empty-icon d-block mb-3" />
        <h5 className="fw-semibold mb-1">No hay formaciones registradas.</h5>
        <p className="text-muted mb-0">Crea una formación o ajusta los filtros.</p>
      </div>
    );
  }

  const emptyRows = Math.max(0, minRows - formaciones.length);
  const hasActions = onViewHistorial || canEdit || canDelete;

  return (
    <div className="management-card dashboard-table-card formaciones-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <p className="dashboard-label mb-1">Listado</p>
          <h6 className="mb-0">Formaciones académicas</h6>
        </div>
        {reload && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload} disabled={loading}>
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        )}
      </div>
      <div className="management-table-wrapper" style={{ maxHeight }}>
        <table className="table align-middle management-table matriculas-table mb-0">
          <thead className="bg-transparent">
            <tr>
              <th>#</th>
              <th>Estudiante</th>
              <th>Matrícula</th>
              <th>Centro</th>
              <th>Estado</th>
              <th>Fechas</th>
              {hasActions && <th className="text-end">Acciones</th>}
            </tr>
          </thead>
          <tbody ref={wrapRef}>
            {formaciones.map((form, idx) => {
              const isSelected = seleccionadoId === form.id;
              const isMenuOpen = openMenuId === form.id;
              return (
                <tr
                  key={`form-${form.id}-${idx}`}
                  className={isSelected ? "selected-row" : ""}
                  onClick={() => onSelect?.(form)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect?.(form);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td className="fw-semibold">{idx + 1}</td>
                  <td>
                    <div className="fw-semibold">{nombreCompleto(form)}</div>
                  </td>
                  <td>
                    <div className="fw-semibold">
                      {form.codigoMatricula || (form.matriculaId ? `#${form.matriculaId}` : "-")}
                    </div>
                    <small className="text-muted">
                      Creada: {formatDate(form.createdAt)}
                    </small>
                  </td>
                  <td>{form.centro || form.centroFormacion || "-"}</td>
                  <td>
                    <span className="badge rounded-pill bg-light text-dark border fw-semibold">
                      {estadoFormacionLabel(form.estado)}
                    </span>
                    {!form.activo && (
                      <span className="badge rounded-pill bg-secondary-subtle text-muted ms-2">Inactiva</span>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <small className="text-muted">Inicio: {formatDate(form.fechaInicio)}</small>
                      <small className="text-muted">Fin: {formatDate(form.fechaFin)}</small>
                      {form.fechaAprobacion && (
                        <small className="text-success">Aprobado: {formatDate(form.fechaAprobacion)}</small>
                      )}
                    </div>
                  </td>
                  {hasActions && (
                    <td className="text-end">
                      <div className="management-actions-wrap">
                        <button
                          type="button"
                          className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isMenuOpen) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            }
                            setOpenMenuId(isMenuOpen ? null : form.id);
                          }}
                          title="Acciones"
                          aria-expanded={isMenuOpen}
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>
                        {isMenuOpen && (
                          <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                            {onViewHistorial && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onViewHistorial(form); }}
                                >
                                  <i className="bi bi-clock-history" />
                                  Historial
                                </button>
                              </li>
                            )}
                            {(onViewHistorial && (canEdit || canDelete)) && (
                              <li className="menu-divider" />
                            )}
                            {canEdit && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onEdit?.(form); }}
                                >
                                  <i className="bi bi-pencil-square" />
                                  Editar
                                </button>
                              </li>
                            )}
                            {canDelete && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item is-danger"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onDelete?.(form); }}
                                >
                                  <i className="bi bi-trash" />
                                  Eliminar
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {emptyRows > 0 &&
              Array.from({ length: emptyRows }).map((_, idx) => (
                <tr key={`form-empty-${idx}`} className="table-empty-row" aria-hidden="true">
                  <td colSpan={hasActions ? 7 : 6}>&nbsp;</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
